import queryString from 'query-string';

/**
 * Carrying the attempted destination across the login flow.
 *
 * A route that `requires: 'login'` is simply absent from the router for an
 * anonymous visitor, so the app falls through to its default route and the URL
 * the person actually opened is gone. For most screens that only costs a click.
 * For the MCP consent screen it breaks the product: the backend 302s the browser
 * to /agents/oauth/authorize?auth_request=<jwt>, and dropping that URL leaves the
 * OAuth handshake half-finished — the client waits on a callback that never
 * comes, and the person is looking at the home page with nothing to tell them the
 * connection was never completed.
 */
export const NEXT_PARAM = 'next';

/**
 * Whether a `next` value is safe to navigate to.
 *
 * Only app-relative paths are accepted. `next` arrives from the URL, so anyone
 * can put anything in it; without this an attacker-supplied absolute URL would
 * turn our login page into an open redirect that lands on their site wearing our
 * domain in the referrer.
 */
export function isSafeNextPath(value?: string | null): value is string {
  if (!value) return false;
  if (!value.startsWith('/')) return false;   // absolute URLs, scheme-relative, bare words
  if (value.startsWith('//')) return false;   // "//evil.com" — another origin
  if (value.includes('\\')) return false;     // browsers normalise "\" to "/" — "/\evil.com"
  return true;
}

/** The `next` carried by a location's query string, or null when absent/unsafe. */
export function readNextPath(search?: string | null): string | null {
  const raw = queryString.parse(search || '')[NEXT_PARAM];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isSafeNextPath(value) ? value : null;
}

/** A location rendered as a `next` value: path + query + hash, nothing else. */
export function locationAsNextPath(
  location: { pathname: string, search?: string, hash?: string }
): string {
  return `${location.pathname}${location.search || ''}${location.hash || ''}`;
}

/** `path` with `next` appended, or `path` untouched when there is nothing to carry. */
export function withNextPath(path: string, next?: string | null): string {
  if (!isSafeNextPath(next)) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${NEXT_PARAM}=${encodeURIComponent(next)}`;
}
